import { ReactNode, useCallback, useEffect, useState } from "react"
import { Box, Checkbox, Collapse, FormControlLabel, Skeleton, Stack, Typography } from "@mui/material"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faChevronDown, faChevronUp } from "@fortawesome/pro-light-svg-icons"
import { makeStyles } from "tss-react/mui"
import { TransitionGroup } from "react-transition-group"
import { useInView } from "react-intersection-observer"

import { useMoney } from "../../../../../../currency/useMoney"
import { toDinero } from "../../../../../../currency/currencyLib"
import { LocalizedEnum, LocalizedStrict } from "../../../../../../localization/components/AppLocalized"
import { StandardAlertInfo } from "../../../../../../../shared/components/alert/StandardAlertInfo"
import { DeliveryType, MeetingServiceOrder } from "../../meetingServiceTypes"
import { SimpleStatusChip } from "../../components/StatusMenu"
import { MeetingServiceOrdersDependencies } from "../../components/OrderDeliveryList"
import { CreateOrderCloseConfirmDialog } from "../../../../../../../shared/components/createOrder/components/CreateOrderCloseConfirmDialog"
import { SingleActiveEditProvider } from "../../../../../../../shared/components/singleActiveEdit/SingleActiveEdit"
import {
    isDeliveredStatus,
    LOCATION_UNKNOWN,
    ProductionDateSection,
    ProductionGroupBy,
    ProductionLayout,
} from "../helpers"
import { ProductionReadyProgress } from "./ProductionReadyProgress"
import { SavedOrderInfo } from "../useProductionOrderEdit"
import { ProductionOrderCard } from "./ProductionOrderCard"

const useStyles = makeStyles()((theme) => ({
    body: {
        flex: 1,
        padding: "1.5rem 0",
    },
    selectAll: {
        marginLeft: ".5rem",
        marginBottom: ".75rem",
    },
    content: {
        width: "100%",
        paddingBottom: "4rem",
    },
    statusGroupChip: {
        alignSelf: "flex-start",
    },
    hourHeader: {
        position: "sticky",
        top: "64px",
        zIndex: 2,
        gap: ".5rem 1rem",
        minHeight: "4rem",
        padding: ".75rem 1rem",
        borderRadius: ".625rem",
        background: theme.palette.tailwind.blue[100],
        cursor: "pointer",
    },
    hourHeaderFullscreen: {
        top: "5.2rem",
        [theme.breakpoints.down("lg")]: {
            top: 0,
        },
    },
    hourHeaderRight: {
        marginLeft: "auto",
    },
    hourChevron: {
        color: theme.palette.tailwind.slate[700],
        fontSize: "1.25rem",
        width: "1.25rem",
        height: "1.25rem",
    },
    hourLabel: {
        fontWeight: 700,
        color: theme.palette.tailwind.slate[800],
    },
    readyDivider: {
        flexShrink: 0,
        width: "1px",
        height: "1.25rem",
        margin: "0 1.5rem",
        background: theme.palette.tailwind.slate[300],
    },
    ordersCount: {
        fontWeight: 600,
        color: theme.palette.tailwind.slate[500],
    },
    empty: {
        padding: "4rem 1rem",
        textAlign: "center",
        color: theme.palette.tailwind.slate[500],
    },
    skeleton: {
        borderRadius: ".75rem",
    },
    savedAlertSticky: {
        position: "sticky",
        top: "138px",
        zIndex: 1,
    },
    savedAlert: {
        width: "100%",
        marginBottom: ".75rem",
        alignItems: "center",
    },
}))

type Props = Readonly<{
    isInitialLoading: boolean
    isEmpty: boolean
    layout: ProductionLayout
    selectedOrderIds: number[]
    onToggleSelect: (orderId: number) => void
    onSetSelection: (orderIds: number[]) => void
    onEditOrder: (orderId: number) => void
    onDeleteOrder: (order: MeetingServiceOrder) => void
    onOrderSaved: (info: SavedOrderInfo) => void
    savedInfo: SavedOrderInfo | null
    onDismissSaved: () => void
    meetingServiceDependencies: MeetingServiceOrdersDependencies
    isFullscreen: boolean
}>

const LOCATION_DELIVERY_TYPES: ReadonlyArray<string> = [DeliveryType.EATING_IN_CANTEEN, DeliveryType.PICK_UP]

const collectLayoutOrders = (layout: ProductionLayout): MeetingServiceOrder[] => {
    if (layout.mode === ProductionGroupBy.STATUS) return layout.statusGroups.flatMap((group) => group.orders)
    if (layout.mode === ProductionGroupBy.LOCATION)
        return layout.locationSections.flatMap((section) => section.locationGroups.flatMap((group) => group.orders))
    if (layout.mode === ProductionGroupBy.DATE) return layout.dateGroups.flatMap((group) => group.orders)
    return layout.sections.flatMap((section) => section.hourGroups.flatMap((group) => group.orders))
}

// Match the Service Center check-in success alert auto-dismiss (SUCCESS_ALERT_MS).
const SAVED_BANNER_AUTO_DISMISS_MS = 30_000

// LazyCard defers a card's mount until it scrolls near the viewport
const useLazyCardStyles = makeStyles()(() => ({
    placeholder: {
        minHeight: "9rem",
    },
}))

function LazyCard({ children }: Readonly<{ children: ReactNode }>) {
    const { classes, cx } = useLazyCardStyles()
    const { ref, inView } = useInView({ triggerOnce: true, rootMargin: "800px 0px" })
    return (
        <div ref={ref} className={cx(!inView && classes.placeholder)}>
            {inView ? children : null}
        </div>
    )
}

export function ProductionViewBody({
    isInitialLoading,
    isEmpty,
    layout,
    selectedOrderIds,
    onToggleSelect,
    onSetSelection,
    onEditOrder,
    onDeleteOrder,
    onOrderSaved,
    savedInfo,
    onDismissSaved,
    meetingServiceDependencies,
    isFullscreen,
}: Props) {
    const { classes, cx } = useStyles()
    const moneyFactory = useMoney({})
    const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(new Set())

    const visibleOrderIds = collectLayoutOrders(layout).map((order) => order.id)
    const allSelected =
        visibleOrderIds.length > 0 && visibleOrderIds.every((orderId) => selectedOrderIds.includes(orderId))
    const someSelected = visibleOrderIds.some((orderId) => selectedOrderIds.includes(orderId))

    // Auto-dismiss the saved banner so it never lingers over the list (the close button still works too).
    useEffect(() => {
        if (!savedInfo) return
        const timer = setTimeout(onDismissSaved, SAVED_BANNER_AUTO_DISMISS_MS)
        return () => clearTimeout(timer)
    }, [savedInfo, onDismissSaved])

    const toggleGroup = (key: string) =>
        setCollapsedGroups((prev) => {
            const next = new Set(prev)
            if (next.has(key)) next.delete(key)
            else next.add(key)
            return next
        })

    const renderCard = (order: MeetingServiceOrder) => (
        <LazyCard key={order.id}>
            <ProductionOrderCard
                order={order}
                onEdit={(target) => onEditOrder(target.id)}
                onDelete={onDeleteOrder}
                onSaved={onOrderSaved}
                isSelected={selectedOrderIds.includes(order.id)}
                onToggleSelect={() => onToggleSelect(order.id)}
                meetingServiceDependencies={meetingServiceDependencies}
            />
        </LazyCard>
    )

    // Sticky "order updated" confirmation under the group header
    const renderSavedBanner = useCallback(
        (orders: ReadonlyArray<MeetingServiceOrder>) => (
            <TransitionGroup className={classes.savedAlertSticky}>
                {savedInfo && orders.some((order) => order.id === savedInfo.orderId) ? (
                    <Collapse key={savedInfo.orderId}>
                        <LocalizedStrict
                            id={
                                savedInfo.total
                                    ? "meeting-service-production-view-saved-banner"
                                    : "meeting-service-production-view-saved-banner-no-total"
                            }
                            attrs={{ title: true }}
                            vars={{
                                orderNumber: String(savedInfo.orderId),
                                total: savedInfo.total ? moneyFactory.format(toDinero(savedInfo.total)) : "",
                            }}
                        >
                            <StandardAlertInfo
                                severity="success"
                                onClose={onDismissSaved}
                                className={classes.savedAlert}
                                title=""
                            />
                        </LocalizedStrict>
                    </Collapse>
                ) : null}
            </TransitionGroup>
        ),
        [savedInfo, onDismissSaved, classes, moneyFactory],
    )

    // collapsible group: the blue hour-bar look, reused for both time and location grouping.
    const renderCollapsibleGroup = (key: string, label: ReactNode, orders: ReadonlyArray<MeetingServiceOrder>) => {
        const isCollapsed = collapsedGroups.has(key)
        // "Ready" mirrors the delivery list: an order counts as prepared once it reaches Delivered or Booked.
        const readyCount = orders.filter((order) =>
            isDeliveredStatus(order.deliveryDetails.statusDetails.status),
        ).length

        return (
            <Stack key={key} spacing=".75rem">
                <Stack
                    direction="row"
                    alignItems="center"
                    flexWrap="wrap"
                    className={cx(classes.hourHeader, isFullscreen && classes.hourHeaderFullscreen)}
                    onClick={() => toggleGroup(key)}
                >
                    <Stack direction="row" alignItems="center" spacing=".75rem">
                        <FontAwesomeIcon
                            icon={isCollapsed ? faChevronDown : faChevronUp}
                            className={classes.hourChevron}
                        />
                        {label}
                    </Stack>
                    <Stack direction="row" alignItems="center" className={classes.hourHeaderRight}>
                        <ProductionReadyProgress ready={readyCount} total={orders.length} />
                        <Box className={classes.readyDivider} />
                        <LocalizedStrict
                            id="meeting-service-production-view-hour-group-orders-count"
                            vars={{ count: orders.length }}
                        >
                            <Typography component="span" className={classes.ordersCount}>
                                {orders.length} orders
                            </Typography>
                        </LocalizedStrict>
                    </Stack>
                </Stack>
                <Collapse in={!isCollapsed} timeout="auto" unmountOnExit>
                    {renderSavedBanner(orders)}
                    <Stack spacing="1rem">{orders.map(renderCard)}</Stack>
                </Collapse>
            </Stack>
        )
    }

    const renderHourGroups = (section: ProductionDateSection) =>
        section.hourGroups.map((hourGroup) =>
            renderCollapsibleGroup(
                hourGroup.key,
                <Typography variant="h6" component="span" className={classes.hourLabel}>
                    {hourGroup.timeLabel}
                </Typography>,
                hourGroup.orders,
            ),
        )

    const renderLocationLabel = (key: string) => {
        if (LOCATION_DELIVERY_TYPES.includes(key)) {
            return (
                <LocalizedEnum base="delivery-type-filter" enumValue={key}>
                    <Typography variant="h6" component="span" className={classes.hourLabel}>
                        {key}
                    </Typography>
                </LocalizedEnum>
            )
        }
        if (key === LOCATION_UNKNOWN) {
            return (
                <LocalizedStrict id="meeting-service-production-view-location-unknown">
                    <Typography variant="h6" component="span" className={classes.hourLabel}>
                        Unknown location
                    </Typography>
                </LocalizedStrict>
            )
        }
        return (
            <Typography variant="h6" component="span" className={classes.hourLabel}>
                {key}
            </Typography>
        )
    }

    return (
        <SingleActiveEditProvider
            renderDiscardConfirm={({ open, onKeepEditing, onDiscard }) => (
                <CreateOrderCloseConfirmDialog open={open} onClose={onKeepEditing} onDiscard={onDiscard} />
            )}
        >
            <Box className={classes.body}>
                {!isInitialLoading && !isEmpty && (
                    <LocalizedStrict id="meeting-service-production-view-select-all-orders" attrs={{ label: true }}>
                        <FormControlLabel
                            className={classes.selectAll}
                            control={
                                <Checkbox
                                    checked={allSelected}
                                    indeterminate={someSelected && !allSelected}
                                    onChange={() => onSetSelection(allSelected ? [] : visibleOrderIds)}
                                />
                            }
                            label="Select all"
                        />
                    </LocalizedStrict>
                )}
                <Stack spacing="2rem" className={classes.content}>
                    {isInitialLoading &&
                        Array.from({ length: 4 }).map((_, index) => (
                            <Skeleton key={index} variant="rectangular" height="9rem" className={classes.skeleton} />
                        ))}

                    {isEmpty && (
                        <LocalizedStrict id="meeting-service-production-view-empty">
                            <Typography variant="body1" className={classes.empty}>
                                No orders to produce
                            </Typography>
                        </LocalizedStrict>
                    )}

                    {!isInitialLoading &&
                        layout.mode === ProductionGroupBy.TIME &&
                        layout.sections.map((section) => (
                            <Stack key={section.dateKey} spacing="1.5rem">
                                {renderHourGroups(section)}
                            </Stack>
                        ))}

                    {!isInitialLoading &&
                        layout.mode === ProductionGroupBy.LOCATION &&
                        layout.locationSections.map((section) => (
                            <Stack key={section.dateKey} spacing="1.5rem">
                                {section.locationGroups.map((group) =>
                                    renderCollapsibleGroup(group.key, renderLocationLabel(group.key), group.orders),
                                )}
                            </Stack>
                        ))}

                    {!isInitialLoading &&
                        layout.mode === ProductionGroupBy.STATUS &&
                        layout.statusGroups.map((group) => (
                            <Stack key={group.status} spacing=".75rem">
                                <SimpleStatusChip
                                    status={group.status}
                                    tooltipLabel=""
                                    className={classes.statusGroupChip}
                                />
                                {renderSavedBanner(group.orders)}
                                <Stack spacing="1rem">{group.orders.map(renderCard)}</Stack>
                            </Stack>
                        ))}

                    {!isInitialLoading &&
                        layout.mode === ProductionGroupBy.DATE &&
                        layout.dateGroups.map((group) =>
                            renderCollapsibleGroup(
                                group.key,
                                <Typography variant="h6" component="span" className={classes.hourLabel}>
                                    {group.dateLabel}
                                </Typography>,
                                group.orders,
                            ),
                        )}
                </Stack>
            </Box>
        </SingleActiveEditProvider>
    )
}
